import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LaunchIcon from "@mui/icons-material/Launch";
import AssetService from "../../../apis/AssetService";
import useDebouncedValue from "../../../utils/useDebouncedValue";

const AssetQuickSearchDialog = ({
  open,
  onClose,
  onSelect,
  title = "Find an asset",
  existingTags = [],
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebouncedValue(searchTerm, 300);
  const [selectedTags, setSelectedTags] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedMessage, setCopiedMessage] = useState(null);

  const tagOptions = useMemo(() => {
    const unique = new Set([...(existingTags || []), ...selectedTags]);
    assets.forEach((asset) => {
      (asset.tags || []).forEach((tag) => unique.add(tag));
    });
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [assets, existingTags, selectedTags]);

  useEffect(() => {
    if (!open) {
      setAssets([]);
      setError(null);
      setLoading(false);
      return;
    }

    const fetchAssets = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await AssetService.listAssets({
          search: debouncedSearch || undefined,
          tags: selectedTags,
          limit: 25,
        });
        setAssets(response.items || []);
      } catch (fetchError) {
        console.error("Failed to search assets", fetchError);
        setError(
          fetchError?.response?.data?.message ||
            fetchError?.message ||
            "Unable to load assets right now."
        );
        setAssets([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchAssets();
  }, [debouncedSearch, open, selectedTags]);

  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setSelectedTags([]);
    }
  }, [open]);

  const handleCopy = async (asset) => {
    if (!asset?.url) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(asset.url);
      } else if (typeof document !== "undefined") {
        const textArea = document.createElement("textarea");
        textArea.value = asset.url;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopiedMessage("Asset URL copied to clipboard");
    } catch (copyError) {
      console.error("Unable to copy asset URL", copyError);
      setCopiedMessage("Unable to copy asset URL");
    }
  };

  const handleSelect = (asset) => {
    onSelect?.(asset);
    onClose?.();
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <IconButton onClick={handleClose} aria-label="Close asset search">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Stack spacing={2} direction={{ xs: "column", md: "row" }}>
          <TextField
            label="Search"
            fullWidth
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by title, description, or file name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Autocomplete
            multiple
            freeSolo
            options={tagOptions}
            value={selectedTags}
            onChange={(_, value) => setSelectedTags(value)}
            renderInput={(params) => <TextField {...params} label="Tags" placeholder="Filter by tag" />}
            sx={{ minWidth: { md: 280 } }}
          />
        </Stack>

        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && assets.length === 0 && (
          <Alert severity="info">No assets found. Try a different search.</Alert>
        )}

        {assets.length > 0 && (
          <List dense sx={{ maxHeight: 360, overflowY: "auto" }}>
            {assets.map((asset) => (
              <ListItem
                key={asset.id}
                secondaryAction={
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      edge="end"
                      aria-label="Open asset"
                      onClick={() => asset?.url && window.open(asset.url, "_blank", "noopener")}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                    <IconButton edge="end" aria-label="Copy asset URL" onClick={() => handleCopy(asset)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <Button variant="contained" size="small" onClick={() => handleSelect(asset)}>
                      Use asset
                    </Button>
                  </Stack>
                }
              >
                <ListItemText
                  primary={asset.title || asset.fileName || "Untitled asset"}
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {asset.contentType}
                        {asset.status ? ` • ${asset.status}` : ""}
                      </Typography>
                      {asset.tags && asset.tags.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Tags: {asset.tags.join(", ")}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <Snackbar
        open={Boolean(copiedMessage)}
        autoHideDuration={3000}
        onClose={() => setCopiedMessage(null)}
        message={copiedMessage}
      />
    </Dialog>
  );
};

export default AssetQuickSearchDialog;
